import {
	Children,
	cloneElement,
	FC,
	isValidElement,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState
} from 'react';
import Portal from 'src/components/common/Portal';
import NGText from 'src/styles/theme/components/NGText';
import { StyledNGTooltip, StyledNGTooltipContainer } from './NGTooltip.styles';
import { INGTooltipProps } from './NGTooltip.type';

const NGTooltip: FC<INGTooltipProps> = ({
	children,
	content,
	isDisabled,
	forceHide,
	textProps,
	tree,
	...otherProps
}): any => {
	const [isMounted, setIsMounted] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const timerDelayRef = useRef<NodeJS.Timeout | null>(null);

	const isWindowDefined = typeof window !== 'undefined';

	const uuid = useId();
	const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

	const onMouseEnter = useCallback(
		(event: MouseEvent | TouchEvent) => {
			try {
				if (!isWindowDefined) return;

				const isTactile = 'ontouchstart' in window;

				if (timerRef.current) clearTimeout(timerRef.current);
				if (timerDelayRef.current) clearTimeout(timerDelayRef.current);

				timerDelayRef.current = setTimeout(
					() => {
						if (event.type === 'touchstart') {
							event = event as TouchEvent;
							setMousePos({ x: event.touches[0].clientX, y: event.touches[0].clientY });
						} else {
							event = event as MouseEvent;
							setMousePos({ x: event.clientX, y: event.clientY });
						}
						setIsMounted(true);
					},
					isTactile ? 150 : 500
				);
			} catch {
				// fallback
			}
		},
		[isWindowDefined]
	);

	const hideTooltip = useCallback(() => {
		setIsVisible(false);

		setTimeout(() => {
			setIsMounted(false);
		}, 250);
	}, []);

	const onMouseLeave = useCallback(() => {
		try {
			if (!isWindowDefined) return;

			if (timerDelayRef.current) clearTimeout(timerDelayRef.current);
			if (timerRef.current) clearTimeout(timerRef.current);

			if ('ontouchstart' in window) return;

			timerRef.current = setTimeout(() => {
				hideTooltip();
			}, 10);
		} catch (err) {
			console.error(err);
		}
	}, [isWindowDefined, hideTooltip]);

	const onMouseMove = (event: any) => {
		// detect touch move position
		if (event.type === 'touchmove') {
			setMousePos({ x: event.touches[0].clientX, y: event.touches[0].clientY });
			return;
		}

		setMousePos({ x: event.clientX, y: event.clientY });
	};

	const childrenWithListener = useMemo(() => {
		const isTactile = typeof window !== 'undefined' && 'ontouchstart' in window;

		return Children.map(children, (child) => {
			if (isValidElement(child)) {
				return cloneElement(child, {
					// @ts-ignore
					onMouseEnter: isTactile ? undefined : onMouseEnter,
					onMouseLeave: isTactile ? undefined : onMouseLeave,
					onMouseMove: isTactile ? undefined : onMouseMove,
					onTouchStart: isTactile ? onMouseEnter : undefined,
					onTouchEnd: isTactile ? onMouseLeave : undefined,
					onTouchMove: isTactile ? onMouseMove : undefined
				});
			}
			return child;
		});
	}, [children, onMouseEnter, onMouseLeave]);

	useEffect(() => {
		if (forceHide) {
			hideTooltip();
		}
	}, [forceHide, hideTooltip]);

	useEffect(() => {
		let timeout: NodeJS.Timeout | undefined;

		if (!isMounted || forceHide) {
			return;
		}

		timeout = setTimeout(() => {
			setIsVisible(true);
		}, 10);

		return () => clearTimeout(timeout);
	}, [isMounted, forceHide, uuid]);

	useEffect(() => {
		if ((isWindowDefined && !('ontouchstart' in window)) || !isMounted) return;

		const handleTouchOutside = (event: TouchEvent) => {
			const target = event.target as HTMLElement;
			const container = document.getElementById(`${uuid}`);

			// check if click is outside of container and body classes does not contain 'modal-open'
			if (container && !container.contains(target)) {
				hideTooltip();
			}
		};

		document.addEventListener('touchstart', handleTouchOutside);

		return () => {
			document.removeEventListener('touchstart', handleTouchOutside);
		};
	}, [hideTooltip, isMounted, isWindowDefined, uuid]);

	if (!isWindowDefined || isDisabled) {
		return children;
	}

	const position = {
		left: mousePos.x < window.innerWidth / 2 ? `${mousePos.x + 14}px` : undefined,
		top: mousePos.y < window.innerHeight / 2 ? `${mousePos.y + 6}px` : undefined,
		right: mousePos.x >= window.innerWidth / 2 ? `${window.innerWidth - mousePos.x + 6}px` : undefined,
		bottom: mousePos.y >= window.innerHeight / 2 ? `${window.innerHeight - mousePos.y + 6}px` : undefined
	};

	return (
		<>
			{childrenWithListener}
			{isMounted ? (
				<Portal>
					<StyledNGTooltipContainer
						id={uuid}
						data-cy='tooltip'
						isVisible={isVisible}
						tree={tree}
						style={position}
						{...otherProps}
					>
						<StyledNGTooltip>
							{typeof content === 'string' ? <NGText {...textProps}>{content}</NGText> : content}
						</StyledNGTooltip>
					</StyledNGTooltipContainer>
				</Portal>
			) : null}
		</>
	);
};

export { NGTooltip };
